import React, { FC } from 'react';

import { Box } from '@mui/system';
import { Typography } from '@mui/material';

import {
  AdminLegendVariant,
  IBookedPharmacistData,
  ILongTermContractModel,
  ProfessionalReplacementTypes,
  adminActions,
  convertToDecimals,
  currency,
} from '@pharmaplan/common';
import { ReactComponent as MenuIcon } from '@pharmaplan/common/assets/icons/menuIcon.svg';

import useBookedWorkshiftActions from '../../../../hooks/AvailableWorkshift/useBookedWorkshiftActions';
import { ReplacementLabels, ScreenTypes } from '../../../../helpers/Constants';
import { LocationText } from '../../AvailableWorkshifts/AvailableWorkshifts';
import AdminHourlyRateFooter from '../../../common/AdminHourlyRateFooter';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { renderScreen } from '../../../../actions/drawerActions';
import useSelfService from '../../../../hooks/useSelfService';
import PharmacyCard from '../../../SelfService/PharmacyCard';
import CustomMenuList from '../../../common/CustomMenuList';
import PharmacistBar from '../../../common/PharmacistBar';
import useGetItems from '../../../../hooks/useGetItems';
import WithSkeleton from '../../../common/WithSkeleton';

import styles from './styles';
import strings from '../../../../localization';

interface ISingleBooking {
  item: IBookedPharmacistData;
  image?: string;
}
const successAction = adminActions.getAdminEvent;

const SingleBooking: FC<ISingleBooking> = ({ item, image }) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const {
    logoId,
    pharmacyCity,
    pharmacistHourlyRate,
    workshiftHourlyRate,
    startDate,
    endDate,
    pharmacyName,
    bookingId,
    pharmacistName,
    pharmacyHourlyRate,
    softwareName,
    pharmacyId,
    professionalName,
    replacementType,
    isAutoSyncRecord,
    longTermShiftContractId = '',
    contractEndDate = '',
    contractStartDate = '',
    weekDayHourlyRate = 0,
    weekendHourlyRate = 0,
    externalPharmacistId,
  } = item ?? {};

  const { getImage } = useGetItems();
  const logoImage = getImage(logoId);
  const showSyncTag = isAutoSyncRecord || !!externalPharmacistId;
  const name = isSelfService ? professionalName ?? '' : pharmacistName;

  const goToPharmacyDetails = () => {
    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.bookingList,
        eventId: bookingId,
        extraParameters: {
          isAutoSyncRecord,
          externalPharmacistId,
        },
      }),
    );
  };

  const goToBookingList = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.bookingList,
        date: startDate,
      }),
    );
  };

  const goToViewDetails = () => {
    dispatch(
      renderScreen({
        screenNumber: 3,
        screenType: ScreenTypes.availableWorkshift,
        eventId: bookingId,
        type: AdminLegendVariant.booked,
        back: goToBookingList,
        pharmacyId,
        extraParameters: {
          isAutoSyncRecord,
          externalPharmacistId,
        },
      }),
    );
  };

  const SkeletonTypography = WithSkeleton({ successAction, variant: 'text' })(
    Typography,
  );

  const { actions } = useBookedWorkshiftActions(
    goToBookingList,
    isAutoSyncRecord,
    externalPharmacistId,
  );

  const longTermShiftContract: ILongTermContractModel = {
    startDate: contractStartDate,
    endDate: contractEndDate,
    longTermShiftContractId,
    weekDayPaidHourlyRate: weekDayHourlyRate,
    weekendPaidHourlyRate: weekendHourlyRate,
    allowanceNotes: '',
    createDate: '',
    updateDate: null,
  };

  const rateLabel = longTermShiftContractId
    ? `${currency}${weekDayHourlyRate}/${currency}${weekendHourlyRate}`
    : (strings.formatString(
      strings.hourlyRateSuffix,
        convertToDecimals(pharmacistHourlyRate) as number,
    ) as string);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <PharmacyCard
          longTermShiftContract={longTermShiftContract}
          isAutoSyncRecord={showSyncTag}
          customContainerStyle={styles.mainContainer}
          event={{
            logoImage,
            hourlyRate: pharmacyHourlyRate ?? workshiftHourlyRate,
            ...item,
          }}
          CustomRightItem={(
            <CustomMenuList
              margin={10}
              customMenuTextStyle={styles.menuText}
              customIconButtonStyle={styles.button}
              items={actions(bookingId, pharmacyId, {
                startDate,
                pharmacyName,
                endDate,
              })}
            >
              <MenuIcon height={24} width={24} />
            </CustomMenuList>
          )}
          CustomContent={(
            <LocationText
              containerStyle={styles.footerContainer}
              label={(
                <SkeletonTypography sx={styles.footer}>
                  {pharmacyCity}
                </SkeletonTypography>
              )}
            />
          )}
          CustomFooter={(
            <>
              <Box sx={styles.footerBox}>
                <AdminHourlyRateFooter
                  pharmacyHourlyRate={pharmacyHourlyRate}
                  successAction={successAction}
                  software={softwareName}
                  replacementType={replacementType}
                />
              </Box>
              <PharmacistBar
                hideHourly={false}
                name={name}
                successAction={successAction}
                hourlyRate={rateLabel}
                title={
                  ReplacementLabels[
                    replacementType ?? ProfessionalReplacementTypes.pharmacist
                  ]
                }
                image={image ?? ' '}
                type={AdminLegendVariant.booked}
                arrowClick={goToPharmacyDetails}
              />
            </>
          )}
          successAction={successAction}
          type={AdminLegendVariant.booked}
          contentClick={goToViewDetails}
        />
      </Box>
    </Box>
  );
};

export default SingleBooking;
